import AxiosInstance from '../../../helpers/axios-instance/axios-instance';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import type { EventGraph, EventGraphEdgeTasksPayload, EventGraphPayload } from './event-graph-client.type';

const axiosInstance = AxiosInstance();
const eventGraphBaseURL = import.meta.env.VITE_APP_API_URL + '/api/event-graph/';

/**
 * Fetches event graph data from the API based on the provided filters.
 *
 * This function constructs the appropriate URL with query parameters based on the provided
 * payload and makes a GET request to the event-graph API endpoint.
 *
 * @param {EventGraphPayload} payload - The filter parameters for the event graph
 * @param {string|undefined} payload.project_id - The ID of the project/team to filter by
 * @param {string|undefined} payload.board_id - The ID of the board/subproject to filter by
 * @param {string|undefined} payload.task_type - The type of tasks to filter by
 * @param {string|undefined} payload.epic_id - The ID of the epic to filter by
 * @param {string|undefined} payload.workflow_id - The ID of the workflow to filter by
 * @param {string} payload.start_date - The start date for the date range filter (YYYY-MM-DD)
 * @param {string} payload.end_date - The end date for the date range filter (YYYY-MM-DD)
 * @param {string} payload.statuses - Comma-separated string of statuses to filter by
 *
 * @returns {Promise<EventGraph>} A promise that resolves to the event graph data
 * @throws {Object} An error object with the API response if the request fails
 */
async function getEventGraph({
  project_id,
  board_id,
  task_type,
  epic_id,
  workflow_id,
  start_date,
  end_date,
  statuses,
}: EventGraphPayload): Promise<EventGraph> {
  let apiURL = `${eventGraphBaseURL}?start_date=${start_date}&end_date=${end_date}&statuses=${encodeURIComponent(statuses)}`;

  if (project_id) {
    apiURL += `&project_id=${project_id}`;
  } else if (workflow_id) {
    apiURL += `&workflow_id=${workflow_id}`;
  }

  if (board_id) {
    apiURL += `&subproject_id=${board_id}`;
  }
  if (task_type) {
    apiURL += `&task_type=${task_type}`;
  }
  if (epic_id) {
    apiURL += `&epic_id=${epic_id}`;
  }
  return await axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Fetches tasks associated with a specific edge in the event graph.
 *
 * This function constructs the appropriate URL with query parameters based on the provided
 * payload and makes a GET request to the event-graph/tasks API endpoint.
 *
 * @param {EventGraphEdgeTasksPayload} payload - The filter parameters for the edge tasks
 * @param {string|undefined} payload.project_id - The ID of the project/team to filter by
 * @param {string|undefined} payload.board_id - The ID of the board/subproject to filter by
 * @param {string|undefined} payload.task_type - The type of tasks to filter by
 * @param {string|undefined} payload.epic_id - The ID of the epic to filter by
 * @param {string|undefined} payload.workflow_id - The ID of the workflow to filter by
 * @param {string} payload.start_date - The start date for the date range filter (YYYY-MM-DD)
 * @param {string} payload.end_date - The end date for the date range filter (YYYY-MM-DD)
 * @param {string} payload.statuses - Comma-separated string of statuses to filter by
 * @param {string} payload.edge_id - The ID of the edge to fetch tasks for
 *
 * @returns {Promise<TaskRecord[]>} A promise that resolves to an array of task records
 * @throws {Object} An error object with the API response if the request fails
 */
async function getEventGraphEdgeTasks({
  project_id,
  board_id,
  task_type,
  epic_id,
  workflow_id,
  start_date,
  end_date,
  statuses,
  edge_id,
}: EventGraphEdgeTasksPayload): Promise<TaskRecord[]> {
  let apiURL = `${eventGraphBaseURL}tasks/?start_date=${start_date}&end_date=${end_date}&statuses=${encodeURIComponent(statuses)}&edge_id=${edge_id}`;

  if (project_id) {
    apiURL += `&project_id=${project_id}`;
  } else if (workflow_id) {
    apiURL += `&workflow_id=${workflow_id}`;
  }

  if (board_id) {
    apiURL += `&subproject_id=${board_id}`;
  }
  if (task_type) {
    apiURL += `&task_type=${task_type}`;
  }
  if (epic_id) {
    apiURL += `&epic_id=${epic_id}`;
  }

  return await axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

export { getEventGraph, getEventGraphEdgeTasks };
